import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, Calendar, Clock, FileText, ArrowRight, X } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { EmailCaptureModal } from "@/components/home/EmailCaptureModal";
import { useContactForm } from "@/components/ContactFormModal";
import { ReportJsonLd } from "@/components/seo/PageJsonLd";

import coverEn from "@/assets/report/cover-en.png";
import tocEn from "@/assets/report/toc-en.png";
import coverDe from "@/assets/report/cover-de.png";
import tocDe from "@/assets/report/toc-de.png";

const reportImages = {
  en: { cover: coverEn, toc: tocEn },
  de: { cover: coverDe, toc: tocDe },
};

const Insights = () => {
  const { t, i18n } = useTranslation();
  const { openContactForm } = useContactForm();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<"en" | "de">("en");
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  const currentLang = (i18n.language?.split("-")[0] || "en") as "en" | "de" | "fr" | "it" | "es";
  const lang = currentLang === "de" ? "de" : "en";
  const isDe = currentLang === "de";
  const isFr = currentLang === "fr";
  const isIt = currentLang === "it";
  const isEs = currentLang === "es";
  const images = reportImages[lang];

  const handleDownloadClick = (language: "en" | "de") => {
    setSelectedLanguage(language);
    setIsEmailModalOpen(true);
  };

  const topics = t("insights.topics", { returnObjects: true }) as string[];

  return (
    <article>
      <ReportJsonLd title={t("insights.reportTitle")} description={t("insights.reportSubtitle")} path="/insights" />
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="secondary" className="mb-4">
              {t("insights.badge")}
            </Badge>
            <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              {t("insights.title")}
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              {t("insights.description")}
            </p>
          </div>
        </div>
      </section>

      {/* Report Preview — visual teaser */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="container">
          <div className="mx-auto max-w-5xl">
            <div className="grid gap-8 md:gap-12 lg:grid-cols-2 items-start">
              {/* Report page previews */}
              <div className="flex justify-center gap-4 md:gap-6">
                <button
                  onClick={() => setZoomedImage(images.cover)}
                  className="relative w-[45%] max-w-[240px] rotate-[-2deg] transition-transform hover:rotate-0 hover:scale-105 duration-300 cursor-zoom-in"
                >
                  <div className="absolute -inset-2 rounded-lg bg-primary/10 blur-xl" />
                  <img
                    src={images.cover}
                    alt={t("insights.reportTitle")}
                    className="relative rounded-lg border border-border shadow-xl"
                    loading="lazy"
                  />
                </button>
                <button
                  onClick={() => setZoomedImage(images.toc)}
                  className="relative w-[45%] max-w-[240px] rotate-[2deg] mt-8 transition-transform hover:rotate-0 hover:scale-105 duration-300 cursor-zoom-in"
                >
                  <div className="absolute -inset-2 rounded-lg bg-primary/5 blur-xl" />
                  <img
                    src={images.toc}
                    alt={t("insights.whatsInside")}
                    className="relative rounded-lg border border-border shadow-xl"
                    loading="lazy"
                  />
                </button>
              </div>

              {/* Report details & download */}
              <div className="flex flex-col justify-center space-y-6">
                <div className="flex flex-wrap gap-2">
                  <Badge>{t("insights.featured")}</Badge>
                  <Badge variant="outline">{t("insights.newFor2026")}</Badge>
                </div>

                <div>
                  <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                    {t("insights.reportTitle")}
                  </h2>
                  <p className="mt-2 text-base text-muted-foreground">
                    {t("insights.reportSubtitle")}
                  </p>
                </div>

                <div>
                  <h4 className="mb-2 text-sm font-semibold text-foreground">
                    {t("insights.whatsInside")}
                  </h4>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    {topics.map((topic, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {isDe ? "Januar 2026" : isFr ? "janvier 2026" : isIt ? "gennaio 2026" : isEs ? "enero de 2026" : "January 2026"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {isDe ? "25 Min. Lesezeit" : isFr ? "25 min de lecture" : isIt ? "25 min di lettura" : isEs ? "25 min de lectura" : "25 min read"}
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5" />
                    {isDe ? "29 Quellen zitiert" : isFr ? "29 sources citées" : isIt ? "29 fonti citate" : isEs ? "29 fuentes citadas" : "29 sources cited"}
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    onClick={() => handleDownloadClick("en")}
                    className="gap-2 w-full sm:w-auto"
                  >
                    <Download className="h-4 w-4" />
                    {t("common.downloadEN")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadClick("de")}
                    className="gap-2 w-full sm:w-auto"
                  >
                    <Download className="h-4 w-4" />
                    {t("common.downloadDE")}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  {t("insights.downloadNote")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Findings */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-8 text-center text-2xl font-bold text-foreground md:text-3xl">
              {t("insights.keyFindings")}
            </h2>

            <div className="grid gap-6 md:grid-cols-3">
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="mb-2 text-4xl font-bold text-primary">
                    {t("insights.stats.market.value")}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t("insights.stats.market.description")}
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="mb-2 text-4xl font-bold text-primary">
                    {t("insights.stats.success.value")}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t("insights.stats.success.description")}
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="mb-2 text-4xl font-bold text-primary">
                    {t("insights.stats.output.value")}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t("insights.stats.output.description")}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">
              {t("insights.discussFindings")}
            </h2>
            <p className="mt-4 text-muted-foreground">
              {t("insights.discussDescription")}
            </p>
            <div className="mt-8">
              <Button
                size="lg"
                className="w-full sm:w-auto"
                onClick={() =>
                  openContactForm({
                    page: "Insights",
                    button: "Book a Strategy Call",
                  })
                }
              >
                {t("insights.bookStrategyCall")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={!!zoomedImage} onOpenChange={() => setZoomedImage(null)}>
        <DialogContent className={`max-w-[90vw] sm:max-w-3xl sm:w-auto p-1 sm:p-2 rounded-lg bg-background/95 backdrop-blur-sm border-border ${zoomedImage === images.toc ? "[&>button]:text-white [&>button]:hover:text-white/80" : ""}`}>
          {zoomedImage && (
            <img
              src={zoomedImage}
              alt={t("insights.reportTitle")}
              className="max-h-[85vh] w-full sm:w-auto rounded-md"
            />
          )}
        </DialogContent>
      </Dialog>

      <EmailCaptureModal
        open={isEmailModalOpen}
        onOpenChange={setIsEmailModalOpen}
        reportLanguage={selectedLanguage}
        source="Insights"
      />
    </article>
  );
};

export default Insights;
